import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";

import { AgGridReact } from "ag-grid-react";
import {
  EllipsisVertical,
  Globe,
  Mail,
  MoreVertical,
  Pencil,
  Power,
  PowerOff,
  Tag,
  FileText,
  CreditCard,
  Gauge,
  AlertTriangle,
  CheckCircle,
  Flame,
  Settings2,
} from "lucide-react";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import DataTable from "@/components/data-table";
import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import InitialPageVisitGuideModal from "@/components/page-visit-guide";
import MailboxWizard from "@/components/popups/add-mailbox";
import { ConfirmActionPopup } from "@/components/popups/confirm-action";
import { LabelsDropdown } from "@/components/popups/labels-dropdown";
import { ConsolidatedFilterDropdown } from "@/components/ui/consolidated-filter-dropdown";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchInput } from "@/components/ui/search-input";
import { useLabels } from "@/hooks/use-labels";
import {
  useBulkDeleteMailboxes,
  useBulkUpdateMailboxes,
  useBulkUpdateMailboxWarmup,
  useBulkWarmupAccountToggle,
  useDeleteMailbox,
  useMailboxes,
  useMailboxStats,
  useDistinctEspTypes,
  useEnableWarmupWithSettings,
  useBulkEnableWarmupWithSettings,
} from "@/hooks/use-mailbox";
import { usePermission } from "@/hooks/use-premission";
import { cn, dateFormatter, extractErrorMessage } from "@/lib/utils";
import AssignSubscriptionSelect from "@/pages/mailbox/assign-subscripiton-select";
import { AssignTemplatesDialog } from "@/components/popups/assign-templates-dialog";
import { AssignPlansDialog } from "@/components/popups/assign-plans-dialog";
import { WarmupSpeedDialog } from "@/components/popups/warmup-speed-dialog";
import { WarmupB2BSettingsDialog } from "@/components/popups/warmup-b2b-settings-dialog";
import { espTypeDescription, LabelType } from "@/types/common";
import { AuthProvider, B2BWarmupSettings, ISenderProfile, MailboxType } from "@/types/mailbox.types";
import { Funnel, Plugs, PlugsConnected } from "@phosphor-icons/react";
import { ColDef } from "ag-grid-community";
import { toast } from "sonner";
import ManageLabels from "../labels/manage-label";
import { PermissionValues } from "../role/constants";
import LabelCellRenderer from "./label-cell-renderer";
import WarmupToggleCell from "./warmup-toggle-cell";
import { WarmupStrategyCell } from "./warmup-strategy-cell";
import Trash from "@/components/icons/trash";
import {
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
  Tooltip,
} from "@/components/ui/tooltip";
import { authProviderIcons } from "./esp-providers";
import { useGetActiveSubscriptionPlans } from "@/hooks/use-subscription";
import ScoreBadge from "@/components/score-badge";
import InboxFilterPopup from "@/components/popups/inbox-filter-popup";

const mailboxColDefs = ({
  pageMetadata,
  hasPermission,
  handleEdit,
  handleDelete,
  onInboxFilterClick,
  onB2BSettingsClick,
  onEnableWarmupRequest,
  subscriptionPlans,
}: {
  pageMetadata: {
    page: number;
    limit: number;
    selectedLabels: string[];
    selectedEspTypes: string[];
  };
  hasPermission: (permission: PermissionValues) => boolean;
  handleEdit: (mailbox: MailboxType) => void;
  handleDelete: (mailbox: MailboxType) => void;
  onInboxFilterClick: (provider: "gmail" | "outlook" | "yahoo") => void;
  onB2BSettingsClick: (mailbox: MailboxType) => void;
  onEnableWarmupRequest: (data: MailboxType | ISenderProfile) => void;
  subscriptionPlans?: any[];
}): ColDef<MailboxType>[] => [
  {
    headerName: "Warmup",
    field: "isWarmupEnabled",
    maxWidth: 80,
    filter: false,

    cellRenderer: (params: { data: MailboxType }) => (
      <WarmupToggleCell
        data={params.data}
        type="mailbox"
        onBeforeEnableWarmup={onEnableWarmupRequest}
      />
    ),
  },

  {
    field: "email",
    resizable: true,
    minWidth: 200,
    cellRenderer: (params: { data: MailboxType }) => {
      return (
        <Link
          to={`/mailboxes/$mailboxid`}
          params={{ mailboxid: params.data._id }}
        >
          {params.data.email}
        </Link>
      );
    },
  },
  {
    field: "firstName",
    headerName: "First Name",
    minWidth: 190,
    cellRenderer: (params: { data: MailboxType }) => {
      return <span>{params.data.firstName}</span>;
    },
  },
  {
    field: "lastName",
    headerName: "Last Name",
    minWidth: 190,
    cellRenderer: (params: { data: MailboxType }) => {
      return <span>{params.data.lastName}</span>;
    },
  },
  {
    field: "isActive",
    headerName: "Status",
    minWidth: 190,
    cellRenderer: (params: { data: MailboxType }) => {
      const connected = params.data.isActive;
      return (
        <div className="h-full flex items-center justify-start">
          {connected ? (
            <Badge
              variant="default"
              className="bg-green-100 hover:bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 h-fit rounded-md space-x-1 shadow-none"
            >
              <PlugsConnected size={12} />
              <span>Connected</span>
            </Badge>
          ) : (
            <Badge
              variant="default"
              className="bg-red-100 hover:bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 h-fit rounded-md space-x-1 shadow-none"
            >
              <Plugs size={12} />
              <span>Disconnected</span>
            </Badge>
          )}
        </div>
      );
    },
  },

  {
    field: "currentDailyLimit",
    headerName: "Today's Temperature",
    minWidth: 100,
    cellRenderer: (params: { data: MailboxType }) => {
      return <span className="tabular-nums">{params.data.currentDailyLimit ?? "—"}</span>;
    },
  },
  {
    colId: "warmupStrategy",
    headerName: "Warmup Strategy",
    minWidth: 160,
    filter: false,
    cellRenderer: (params: { data: MailboxType }) => (
      <WarmupStrategyCell data={params.data} />
    ),
  },
  {
    field: "authProvider",
    headerName: "ESP Type",
    minWidth: 120,
    cellRenderer: (params: { value: AuthProvider }) => {
      return (
        <div className="flex items-center h-full">
          <Tooltip>
            <TooltipTrigger asChild>
              {authProviderIcons[params.value]?.icon}
            </TooltipTrigger>
            <TooltipContent>
              {authProviderIcons[params.value]?.tooltip}
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
  },
  { field: "smtpHost", minWidth: 190 },
  { field: "smtpPort", minWidth: 190 },
  {
    field: "smtpTls",
    headerName: "SMTP TLS",
    minWidth: 190,
    cellRenderer: (params: { value: boolean }) => {
      if (!params.value && params.value !== false) return null;
      return (
        <Badge
          className={cn(
            "rounded-sm shadow-none",
            params.value
              ? "bg-green-200 hover:bg-green-200 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-200 hover:bg-red-200 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          )}
        >
          {params.value ? "Yes" : "No"}
        </Badge>
      );
    },
  },
  {
    field: "smtpPassword",
    sortable: false,
    filter: false,
    minWidth: 190,
    cellRenderer: () => {
      return <span>********</span>;
    },
  },
  {
    headerName: "Labels",
    field: "labels",
    minWidth: 250,
    cellRenderer: (params: { data: MailboxType }) => (
      <LabelCellRenderer params={params} pageMetadata={pageMetadata} />
    ),
  },
  {
    headerName: "Assigned Subscription",
    // field: "assignedSubscription",
    colId: "assignedSubscription",
    minWidth: 250,
    valueGetter: (params: any) => {
      if (!subscriptionPlans || !params.data) return "";
      
      const { subscriptionId, planId, addonPlan } = params.data;
      
      if (!subscriptionId) return "No subscription assigned";
      
      // Find the subscription
      const subscription = subscriptionPlans.find((sub) => sub._id === subscriptionId);
      if (!subscription) return "Subscription not found";
      
      // Find the plan within the subscription
      const plan = subscription.plans.find((p: any) => {
        // Check for custom plan (addonPlan)
        if (addonPlan && p?.isCustomPlan && p?.addonPlanId?._id === addonPlan) {
          return true;
        }
        // Check for regular plan
        if (planId && p?.planId?._id === planId) {
          return true;
        }
        return false;
      });
      
      if (!plan) return "Plan not found";
      
      // Format the label similar to options useMemo
      if (plan?.isCustomPlan && plan?.addonPlanId?._id) {
        return `Custom Plan (${plan.addonPlanId.maxEmailsPerDay} emails/day) (${subscription.interval})`;
      }
      
      if (plan?.planId?._id) {
        const category = plan.planId.category || "";
        const planType = plan.planId.planType || "";
        return `${category} - ${planType} (${subscription.interval})`;
      }
      
      return "Unknown plan";
    },
    cellRenderer: (params: { data: MailboxType }) => (
      <div className="flex items-center h-full">
        <AssignSubscriptionSelect
          mailboxId={params.data._id}
          mailboxEmail={params.data.email}
          subscriptionId={params.data.subscriptionId}
          planId={params.data.planId}
          addonPlanId={params.data.addonPlan}
          pageType="list"
        />
      </div>
    ),
  },

  {
    headerName: "Created At",
    field: "createdAt",
    minWidth: 190,
    cellRenderer: (params: { data: MailboxType }) => {
      return <span>{dateFormatter(params.data.createdAt)}</span>;
    },
  },
  {
    field: "statusLabel",
    headerName: "Mailbox Status",
    pinned: "right",
    width: 130,
    suppressSizeToFit: true,
    
    cellRenderer: (params: { data: MailboxType }) => {
      const statusLabel = params.data.statusLabel?.toLowerCase() || "";

      const numberofdays = Math.floor((new Date().getTime() - new Date(params.data.warmupStartDate).getTime()) / (1000 * 60 * 60 * 24));
      
      // Map status labels to badge configurations
      const getStatusConfig = () => {
        if (statusLabel.includes("warming up") || statusLabel.includes("warming")) {
          return {
            label: "Warming Up",
            tooltip: `Reputation building in progress — day ${numberofdays} of 21. Please do not start campaigns yet.`,
            className: "bg-yellow-100 hover:bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
            dotColor: "bg-yellow-500",
          };
        }
        if (statusLabel.includes("ready")) {
          return {
            label: "Ready",
            tooltip: "Safe to send. Mailbox is healthy and fully primed.",
            className: "bg-green-100 hover:bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
            dotColor: "bg-green-500",
          };
        }
        if (statusLabel.includes("cool down") || statusLabel.includes("cooldown")) {
          return {
            label: "Cool Down",
            tooltip: "This mailbox entered Cool Down as the last 5 days' warmup spam rate crossed 5%. Pause email campaigns temporarily to protect your domain reputation.",
            className: "bg-red-100 hover:bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
            dotColor: "bg-red-500",
          };
        }
        if (statusLabel.includes("paused")) {
          return {
            label: "Paused",
            tooltip: "Turn on warmups to build reputation",
            className: "bg-blue-100 hover:bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
            dotColor: "bg-blue-500",
          };
        }
        // Default fallback
        return {
          label: params.data.statusLabel || "Unknown",
          tooltip: params.data.statusLabel || "Unknown status",
          className: "bg-gray-100 hover:bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400",
          dotColor: "bg-gray-500",
        };
      };

      const config = getStatusConfig();

      return (
        <div className="h-full flex items-center justify-start">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="default"
                className={cn(
                  "h-fit rounded-md space-x-1.5 shadow-none flex items-center gap-1.5",
                  config.className
                )}
              >
                <span className={cn("w-2 h-2 rounded-full", config.dotColor)} />
                <span>{config.label}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <span>{config.tooltip}</span>
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
  },
  {
    field: "deliveryRate",
    colId: "openPercentages-deliveryRate",
    headerName: "Deliverability Score",
    sortable: true,
    pinned: "right",
    filter: false,
    width: 105,
    suppressSizeToFit: true,
    valueGetter: (params: any) => {
      const gmailScore = params.data?.openPercentages?.inboxGmail || 0;
      const outlookScore = params.data?.openPercentages?.inboxOutlook || 0;
      const yahooScore = params.data?.openPercentages?.inboxYahoo || 0;
      if (!gmailScore && !outlookScore) return 0;
      // return 100;
      // return Math.round((gmailScore + outlookScore + yahooScore) / 3);
      return Math.round((gmailScore + outlookScore) / 2);
    },
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="cursor-pointer flex items-center gap-2 h-full w-20"
              onClick={() => onInboxFilterClick("gmail")}
            >
              <span className="truncate flex-1">Deliverability Score</span>
              <Funnel className="h-4 w-4 flex-shrink-0" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Deliverability Score</span>
          </TooltipContent>
        </Tooltip> 
      );
    },
    cellRenderer: (params: any) => {
      const gmailScore = params.data.openPercentages.inboxGmail || 0;
      const outlookScore = params.data.openPercentages.inboxOutlook || 0;
      const yahooScore = params.data.openPercentages.inboxYahoo || 0;
      const combinedScore = Math.round((gmailScore + outlookScore) / 2);
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={combinedScore}
            thresholds={{ green: 85, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    field: "openPercentages.inboxGmail",
    colId: "openPercentages-gmail",
    headerName: "Google",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 90,
    suppressSizeToFit: true,
    valueGetter: (params: any) =>
      params.data?.openPercentages?.inboxGmail ?? 0,
    // 100,
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="cursor-pointer flex items-center gap-2 h-full"
              // onClick={() => onInboxFilterClick("gmail")}
            >
              <span className="truncate flex-1">Google</span>
              {/* <Funnel className="h-4 w-4 flex-shrink-0" /> */}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Google Reputation Score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.value;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={params.data.openPercentages.inboxGmail}
            thresholds={{ green: 85, yellow: 70 }}
          />
        </div>
      );
    },
  },

  {
    field: "openPercentages.inboxOutlook",
    colId: "openPercentages-outlook",
    headerName: "Microsoft",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 100,
    suppressSizeToFit: true,
    valueGetter: (params: any) =>
      params.data?.openPercentages?.inboxOutlook ?? 0,
    // tooltip: "Microsoft Reputation Score",
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="cursor-pointer flex items-center gap-2 h-full"
              // onClick={() => onInboxFilterClick("outlook")}
            >
              <span className="truncate flex-1">Microsoft</span>
              {/* <Funnel className="h-4 w-4 flex-shrink-0" /> */}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Microsoft Reputation Score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.value;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={params.data.openPercentages.inboxOutlook}
            thresholds={{ green: 85, yellow: 70 }}
          />
        </div>
      );
    },
  },

  {
    field: "openPercentages.inboxYahoo",
    colId: "openPercentages-yahoo",
    headerName: "Yahoo",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 85,
    suppressSizeToFit: true,
    valueGetter: (params: any) =>
      params.data?.openPercentages?.inboxYahoo ?? 0,
    // tooltip: "Microsoft Reputation Score",
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="cursor-pointer flex items-center gap-2 h-full"
              // onClick={() => onInboxFilterClick("yahoo")}
            >
              <span className="truncate flex-1">Yahoo</span>
              {/* <Funnel className="h-4 w-4 flex-shrink-0" /> */}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Yahoo Reputation Score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.value;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={params.data.openPercentages.inboxYahoo}
            thresholds={{ green: 85, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    field: "openPercentages.inboxZoho",
    colId: "openPercentages-zoho",
    headerName: "Zoho",
    sortable: true,
    // pinned: "right",
    filter: false,
    width: 85,
    suppressSizeToFit: true,
    valueGetter: (params: any) =>
      params.data?.openPercentages?.inboxZoho ?? 0,
    // tooltip: "Microsoft Reputation Score",
    headerComponent: () => {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="cursor-pointer flex items-center gap-2 h-full"
              // onClick={() => onInboxFilterClick("yahoo")}
            >
              <span className="truncate flex-1">Zoho</span>
              {/* <Funnel className="h-4 w-4 flex-shrink-0" /> */}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <span>Zoho Reputation Score</span>
          </TooltipContent>
        </Tooltip>
      );
    },
    cellRenderer: (params: any) => {
      const score = params.value;
      return (
        <div className="flex items-center justify-center h-full">
          <ScoreBadge
            score={params.data.openPercentages.inboxZoho}
            thresholds={{ green: 85, yellow: 70 }}
          />
        </div>
      );
    },
  },
  {
    colId: "actions",
    headerName: "Actions",
    sortable: false,
    filter: false,
    maxWidth: 80,
    pinned: "right",
    resizable: false,
    suppressMovable: true,
    cellRenderer: (params: { data: MailboxType }) => {
      const hasEditPermission = hasPermission("mailboxes:edit");
      const hasDeletePermission = hasPermission("mailboxes:delete");

      if (!hasEditPermission && !hasDeletePermission) return null;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasEditPermission && (
              <DropdownMenuItem onSelect={() => onB2BSettingsClick(params.data)}>
                <Settings2 className="h-4 w-4 mr-2" />
                B2B Warmup Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              disabled={!hasDeletePermission}
              onSelect={() => handleDelete(params.data)}
              className="delete-button group"
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
type SelectedPlanMetaData = {
  subscriptionId: string | null;
  planId: string | null;
  value: string | null;
};

const MailboxPageContent = () => {
  const gridRef = useRef<AgGridReact>(null);

  const [limit, setLimit] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState<MailboxType[]>([]);
  const [mailboxToEdit, setMailboxToEdit] = useState<MailboxType | undefined>(
    undefined
  );
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [selectedEspTypes, setSelectedEspTypes] = useState<string[]>([]);
  const [selectedConnectionStatus, setSelectedConnectionStatus] = useState<
    boolean | undefined
  >(undefined);
  const [minInboxPercentage, setMinInboxPercentage] = useState<
    number | undefined
  >(undefined);
  const [maxInboxPercentage, setMaxInboxPercentage] = useState<
    number | undefined
  >(undefined);
  const [mailboxToDelete, setMailboxToDelete] = useState<MailboxType | null>(
    null
  );

  const [selectedPlanMetaData, setSelectedPlanMetaData] = useState<
    string[] | null
  >([]);
  // dialog states
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showSingleDeleteDialog, setShowSingleDeleteDialog] = useState(false);
  const [showEditMailboxDialog, setShowEditMailboxDialog] = useState(false);
  const [selectedWarmupStatus, setSelectedWarmupStatus] = useState<
    boolean | undefined
  >(undefined);
  const [showInboxFilterPopup, setShowInboxFilterPopup] = useState(false);
  const [filterProvider, setFilterProvider] = useState<
    "gmail" | "outlook" | "yahoo" | null
  >(null);
  const { data: mailboxStats } = useMailboxStats();
  const { mutateAsync: deleteMailbox } = useDeleteMailbox();
  const { data: mailboxes, isLoading } = useMailboxes(
    currentPage,
    limit,
    selectedLabels,
    searchText,
    selectedEspTypes,
    selectedWarmupStatus,
    selectedPlanMetaData,
    selectedConnectionStatus,
    minInboxPercentage,
    maxInboxPercentage
  );



  const { mutateAsync: bulkUpdateWarmup, isPending: isBulkWarmupUpdating } =
    useBulkUpdateMailboxWarmup();
  const { mutateAsync: enableWarmupWithSettings } = useEnableWarmupWithSettings();
  const { mutateAsync: bulkEnableWarmupWithSettings } =
    useBulkEnableWarmupWithSettings();
  const {
    mutateAsync: bulkUpdateMailboxes,
    isPending: isBulkUpdatingMailboxes,
  } = useBulkUpdateMailboxes();
  const [showManageLabels, setShowManageLabels] = useState(false);
  const [showAssignTemplates, setShowAssignTemplates] = useState(false);
  const [showAssignPlans, setShowAssignPlans] = useState(false);
  const [showWarmupSpeed, setShowWarmupSpeed] = useState(false);
  const [b2bSettingsMailbox, setB2bSettingsMailbox] = useState<{
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    planAssigned: boolean;
    planMaxDailyLimit?: number;
    /** When opening from bulk (multiple selected), number of selected mailboxes. */
    selectedMailboxCount?: number;
  } | null>(null);
  const [mailboxesToEnableAfterB2B, setMailboxesToEnableAfterB2B] = useState<
    string[]
  >([]);

  const { data: labels } = useLabels(1, 100, LabelType.MAILBOX);
  const { data: distinctEspTypes } = useDistinctEspTypes();
  const { data: subscriptionPlans } = useGetActiveSubscriptionPlans();

  // Allow export trigger from header actions via a window event
  useEffect(() => {
    const handleExport = () => {
      gridRef.current?.api.exportDataAsCsv({ fileName: "mailboxes.csv" });
    };
    window.addEventListener("mailboxes-export-csv", handleExport as EventListener);
    return () => {
      window.removeEventListener("mailboxes-export-csv", handleExport as EventListener);
    };
  }, []);

  const options = useMemo(() => {
    if (!subscriptionPlans) return [];

    // Flatten the nested structure and filter out null values
    // Only include B2B and B2C plans (exclude seed list plans)
    return subscriptionPlans
      .flatMap((subscription) =>
        subscription.plans.map((plan) => {
          // Handle custom plans (these are typically B2C)
          if (plan?.isCustomPlan && plan?.addonPlanId?._id) {
            return {
              key: `${subscription._id}-${plan.addonPlanId._id}`,
              label: `Custom Plan (${plan.addonPlanId.maxEmailsPerDay} emails/day)`,
              value: plan.addonPlanId._id,
              type: "custom",
              emailsPerDay: plan.addonPlanId.maxEmailsPerDay,
              subscriptionId: subscription._id,
              status: subscription.status,
              interval: subscription.interval,
              used: plan.assignedMailboxes?.length || 0,
              total: plan.quantity,
              bgcolor: "#6b7280",
            };
          }

          // Handle regular plans - only include B2B and B2C (exclude seed list plans)
          if (plan?.planId?._id) {
            // Skip seed list plans
            const isSeedListPlan = plan.planId.isOnDemandSenderProfile || false;
            if (isSeedListPlan) {
              return null;
            }
            
            // Only include B2B and B2C plans
            const category = plan.planId.category?.toUpperCase() || "";
            if (category !== "B2B" && category !== "B2C") {
              return null;
            }
            
            return {
              key: `${subscription._id}-${plan.planId._id}`,
              label: `${plan.planId.category} - ${plan.planId.planType}`,
              value: plan.planId._id,
              bgcolor: "#6b7280",
              type: "regular",
              category: plan.planId.category,
              planType: plan.planId.planType,
              subscriptionId: subscription._id,
              status: subscription.status,
              interval: subscription.interval,
              used: plan.assignedMailboxes?.length || 0,
              total: plan.quantity,
            };
          }

          return null;
        })
      )
      .filter(
        (option): option is NonNullable<typeof option> => option !== null
      );
  }, [subscriptionPlans]);

  const distinctEspDescriptions = useMemo(
    () =>
      distinctEspTypes?.map((espType) => ({
        value: espType,
        label: espTypeDescription[espType as keyof typeof espTypeDescription],
        bgcolor: "#6b7280",
      })),
    [distinctEspTypes]
  );
  const warmupStatusOptions = useMemo(
    () => [
      {
        value: "true",
        label: "Warmup Enabled",
        bgcolor: "#6b7280",
      },
      {
        value: "false",
        label: "Warmup Disabled",
        bgcolor: "#6b7280",
      },
    ],
    []
  );
  const connectionStatusOptions = useMemo(
    () => [
      {
        value: "true",
        label: "Connected",
        bgcolor: "#10b981", // green-500
        icon: PlugsConnected,
      },
      {
        value: "false",
        label: "Disconnected",
        bgcolor: "#ef4444", // red-500
        icon: Plugs,
      },
    ],
    []
  );
  const { hasPermission } = usePermission();

  const bulkDeleteMutation = useBulkDeleteMailboxes();

  const headerCardStats = [
    {
      title: "Unique Domains",
      value: mailboxStats?.totalDomains || 0,
      icon: Globe,
    },
    {
      title: "Mailboxes Connected",
      value: mailboxStats?.connected || 0,
      icon: PlugsConnected,
    },
    {
      title: "Mailboxes Disconnected",
      value: mailboxStats?.disconnected || 0,
      icon: Plugs,
    },
    {
      title: "Mailboxes Warmup on",
      value: mailboxStats?.warmupEnabled || 0,
      icon: Power,
    },
    {
      title: "Mailboxes Warmup off",
      value: mailboxStats?.warmupDisabled || 0,
      icon: PowerOff,
    },
    {
      title: "Mailboxes Cool Down",
      value: mailboxStats?.coolDown || 0,
      icon: AlertTriangle,
    },
    {
      title: "Mailboxes Ready",
      value: mailboxStats?.ready || 0,
      icon: CheckCircle,
    },
    {
      title: "Mailboxes Warming Up",
      value: mailboxStats?.warmingUp || 0,
      icon: Flame,
    },
  ];

  // resolved
  const onSelectionChanged = () => {
    const selectedNodes = gridRef?.current?.api.getSelectedNodes();
    const selectedData = selectedNodes?.map((node) => node.data) || [];
    setSelectedRows(selectedData);
  };

  // resolved
  const handleBulkDelete = async () => {
    try {
      const ids = selectedRows.map((row) => row._id);
      await bulkDeleteMutation.mutateAsync(ids);
      toast.success("Selected mailboxes deleted successfully");
      setShowBulkDeleteDialog(false);
      setSelectedRows([]);

      window.cioanalytics?.track("mailboxes_bulk_deleted", {
        count: selectedRows.length,
        emails: selectedRows.map((row) => row.email),
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        "Failed to delete mailboxes"
      );
      toast.error(errorMessage);
    }
  };

  // resolved
  const handleDelete = useCallback(
    (mailbox: MailboxType) => {
      setMailboxToDelete(mailbox);
      setShowSingleDeleteDialog(true);
    },
    [setMailboxToDelete, setShowSingleDeleteDialog]
  );

  // resolved
  const handleEdit = useCallback(
    (mailbox: MailboxType) => {
      setMailboxToEdit(mailbox);
      setShowEditMailboxDialog(true);
    },
    [setMailboxToEdit, setShowEditMailboxDialog]
  );

  // resolved
  const confirmSingleDelete = async () => {
    if (!mailboxToDelete) return;
    try {
      await deleteMailbox(mailboxToDelete._id);
      toast.success("Mailbox deleted successfully");
      setMailboxToDelete(null);
      window.cioanalytics?.track("mailbox_deleted", {
        email: mailboxToDelete.email,
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        "Failed to delete mailbox"
      );
      toast.error(errorMessage);
    }
  };

  // const handleRowClick = (params: any) => {
  //   console.log("Row clicked:", params.data);
  //   };

  // Handle assign templates bulk action
  const handleAssignTemplates = () => {
    setShowAssignTemplates(true);
    
    // Track analytics event
    window.cioanalytics?.track("mailboxes_bulk_assign_templates_clicked", {
      count: selectedRows.length,
      emails: selectedRows.map((row) => row.email),
    });
  };

  // Handle template assignment
  const handleTemplateAssignment = (templateIds: string[]) => {
    // TODO: Implement actual template assignment API call
    toast.success(`Assigned ${templateIds.length} template(s) to ${selectedRows.length} mailbox(es)`);
    
    // Track analytics event
    window.cioanalytics?.track("mailboxes_templates_assigned", {
      templateCount: templateIds.length,
      mailboxCount: selectedRows.length,
      emails: selectedRows.map((row) => row.email),
    });
  };


  // Handle plan assignment bulk action
  const handlePlanAssignment = () => {
    setShowAssignPlans(true);
    window.cioanalytics?.track("mailboxes_bulk_plan_assignment_clicked", {
      count: selectedRows.length,
      emails: selectedRows.map((row) => row.email),
    });
  };

  // Handle plan assignment
  const handlePlanAssignmentSubmit = (planIds: string[]) => {
    // TODO: Implement actual plan assignment API call
    toast.success(`Assigned ${planIds.length} plan(s) to ${selectedRows.length} mailbox(es)`);
    window.cioanalytics?.track("mailboxes_plans_assigned", {
      planCount: planIds.length,
      mailboxCount: selectedRows.length,
      emails: selectedRows.map((row) => row.email),
    });
  };

  // Handle warmup speed bulk action
  const handleWarmupSpeed = () => {
    setShowWarmupSpeed(true);
    
    // Track analytics event
    window.cioanalytics?.track("mailboxes_bulk_warmup_speed_clicked", {
      count: selectedRows.length,
      emails: selectedRows.map((row) => row.email),
    });
  };

  // Handle warmup speed application
  const handleSpeedApplication = (speed: string) => {
    // TODO: Implement actual warmup speed API call
    toast.success(`Applied ${speed} warmup speed to ${selectedRows.length} mailbox(es)`);
    
    // Track analytics event
    window.cioanalytics?.track("mailboxes_warmup_speed_applied", {
      speed: speed,
      mailboxCount: selectedRows.length,
      emails: selectedRows.map((row) => row.email),
    });
  };

  // ------------------- Bulk Actions Mailboxes -------------------

  // resolved
  const handleBulkWarmup = async (enable: boolean) => {
    try {
      await bulkUpdateWarmup({
        ids: selectedRows.map((row) => row._id),
        updates: { isWarmupEnabled: enable },
      });
      toast.success(
        `Warmup ${enable ? "enabled" : "disabled"} for selected mailboxes`
      );

      const eventName = enable
        ? "mailboxes_bulk_warmup_toggled_on"
        : "mailboxes_bulk_warmup_toggled_off";
      window.cioanalytics?.track(eventName, {
        count: selectedRows.length,
        emails: selectedRows.map((row) => row.email),
      });
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Failed to update warmup status"
      );
      toast.error(message);
    }
  };

  // resolved
  const handleBulkConnect = async (connect: boolean) => {
    try {
      await bulkUpdateMailboxes({
        ids: selectedRows.map((row) => row._id),
        updates: { isActive: connect },
      });
      toast.success(
        `Successfully ${
          connect ? "connected" : "disconnected"
        } selected mailboxes`
      );

      const eventName = connect
        ? "mailboxes_bulk_connected"
        : "mailboxes_bulk_disconnected";
      window.cioanalytics?.track(eventName, {
        count: selectedRows.length,
        emails: selectedRows.map((row) => row.email),
      });
    } catch (error) {
      const message = extractErrorMessage(
        error,
        `Failed to ${connect ? "connect" : "disconnect"} mailboxes`
      );
      toast.error(message);
    }
  };

  // resolved
  const handleLabelsChange = (newLabels: string[]) => {
    setSelectedLabels(newLabels);
    setCurrentPage(1);
  };

  const handleEspTypesChange = (newEspTypes: string[]) => {
    setSelectedEspTypes(newEspTypes);
    setCurrentPage(1);
  };

  const handleWarmupStatusChange = (newWarmupStatus: string[] | any[]) => {
    setSelectedWarmupStatus(
      newWarmupStatus[0] === "true"
        ? true
        : newWarmupStatus[0] === "false"
          ? false
          : undefined
    );
    setCurrentPage(1);
  };
  const handlePlanChange = (newPlan: string[]) => {
    setSelectedPlanMetaData(newPlan.length > 0 ? newPlan : ([] as string[]));
    console.log(newPlan, "newPlan");
    setCurrentPage(1);
  };

  const handleConnectionStatusChange = (
    newConnectionStatus: string[] | any[]
  ) => {
    setSelectedConnectionStatus(
      newConnectionStatus[0] === "true"
        ? true
        : newConnectionStatus[0] === "false"
          ? false
          : undefined
    );
    setCurrentPage(1);
  };

  const handleInboxFilterClick = (provider: "gmail" | "outlook" | "yahoo") => {
    setFilterProvider(provider);
    setShowInboxFilterPopup(true);
  };

  const handleInboxFilterApply = (
    min: number | undefined,
    max: number | undefined
  ) => {
    console.log("handleInboxFilterApply called with min:", min, "max:", max);
    setMinInboxPercentage(min);
    setMaxInboxPercentage(max);
    setCurrentPage(1);
    setShowInboxFilterPopup(false);
  };

  const handleInboxFilterClear = () => {
    setMinInboxPercentage(undefined);
    setMaxInboxPercentage(undefined);
    setCurrentPage(1);
    setShowInboxFilterPopup(false);
  };

  const handleB2BSettingsClick = useCallback((mailbox: MailboxType) => {
    setB2bSettingsMailbox({
      id: mailbox._id,
      email: mailbox.email,
      firstName: mailbox.firstName,
      lastName: mailbox.lastName,
      planAssigned: !!(mailbox.planId || mailbox.addonPlan),
      planMaxDailyLimit: mailbox.customWarmupPlan?.maxDailyLimit,
    });
    setMailboxesToEnableAfterB2B([]);
  }, []);

  const handleEnableWarmupRequest = useCallback(
    (data: MailboxType | ISenderProfile) => {
      const mailbox = data as MailboxType;
      setB2bSettingsMailbox({
        id: data._id,
        email: data.email,
        firstName: mailbox.firstName,
        lastName: mailbox.lastName,
        planAssigned: !!(mailbox.planId || mailbox.addonPlan),
        planMaxDailyLimit: mailbox.customWarmupPlan?.maxDailyLimit,
      });
      setMailboxesToEnableAfterB2B([data._id]);
    },
    []
  );

  const handleB2BSaveAndEnableWarmup = useCallback(
    async (settings: B2BWarmupSettings) => {
      if (mailboxesToEnableAfterB2B.length === 0) return;
      try {
        if (mailboxesToEnableAfterB2B.length === 1) {
          await enableWarmupWithSettings({
            mailboxId: mailboxesToEnableAfterB2B[0],
            settings,
          });
        } else {
          await bulkEnableWarmupWithSettings({
            ids: mailboxesToEnableAfterB2B,
            ...settings,
          });
        }
        toast.success(
          `Warmup enabled for ${mailboxesToEnableAfterB2B.length} mailbox(es)`
        );
        setMailboxesToEnableAfterB2B([]);
        window.cioanalytics?.track("mailboxes_bulk_warmup_toggled_on", {
          count: mailboxesToEnableAfterB2B.length,
        });
      } catch (error) {
        const message = extractErrorMessage(
          error,
          "Failed to enable warmup for selected mailboxes"
        );
        toast.error(message);
      }
    },
    [
      mailboxesToEnableAfterB2B,
      enableWarmupWithSettings,
      bulkEnableWarmupWithSettings,
    ]
  );

  const combinedLoading = isBulkWarmupUpdating || isBulkUpdatingMailboxes;

  const pageMetadata = useMemo(() => {
    return {
      page: currentPage,
      limit,
      selectedLabels,
      selectedEspTypes,
      quickFilterText: searchText,
      minInboxPercentage,
      maxInboxPercentage,
    };
  }, [
    currentPage,
    limit,
    selectedLabels,
    selectedEspTypes,
    searchText,
    minInboxPercentage,
    maxInboxPercentage,
  ]);

  // resolved
  const colDefs = useMemo(() => {
    const columnDefs = mailboxColDefs({
      pageMetadata,
      hasPermission,
      handleEdit,
      handleDelete,
      onInboxFilterClick: handleInboxFilterClick,
      onB2BSettingsClick: handleB2BSettingsClick,
      onEnableWarmupRequest: handleEnableWarmupRequest,
      subscriptionPlans,
    });

    // Remove columns per request and based on permissions
    const removedFields = new Set([
      "smtpHost",
      "smtpPort",
      "smtpTls",
      "smtpPassword",
      "labels",
    ]);

    const filtered = columnDefs.filter((col: any) => {
      if (col?.colId === "actions") return false; // hide actions from export/view
      if (col?.field && removedFields.has(col.field)) return false;
      return true;
    });

    return filtered;
  }, [hasPermission, handleEdit, handleDelete, handleB2BSettingsClick, handleEnableWarmupRequest, pageMetadata, subscriptionPlans]);

  // Modify the gridOptions to include quickFilter
  const gridOptions = {
    // ... existing options ...
    quickFilterText: searchText,
  };

  return (
    <>
      <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-4 mb-4">
        {headerCardStats.map((stat, index) => (
          <Card key={index} className="p-4 space-y-1 sm:space-y-2">
            <CardHeader className="p-0">
              <CardTitle className="text-sm font-medium">
                <div className="flex items-center gap-1 sm:gap-2">
                  <div className="border rounded-lg bg-secondary size-8 flex items-center justify-center shrink-0">
                    <stat.icon className="size-4" />
                  </div>
                  <span className="text-sm text-wrap">{stat.title}</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="text-lg sm:text-xl md:text-2xl font-bold">
                {stat.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:justify-between lg:items-center">
        <SearchInput
          onSearch={(searchTerm) => {
            setSearchText(searchTerm);
            setCurrentPage(1);
          }}
          placeholder="Search by email..."
          className="max-w-none sm:w-full md:max-w-[300px]"
        />
        <div className="flex gap-2 w-full flex-row items-center justify-between flex-wrap lg:flex-nowrap">
          <div className="flex gap-2">
            {/* Empty left side for now */}
          </div>

          <div className="flex gap-2 items-center">
            <ConsolidatedFilterDropdown
              labels={
                labels?.data?.map((label) => ({
                  value: label._id,
                  label: label.name,
                  bgcolor: label.color || "",
                })) || []
              }
              selectedLabels={selectedLabels}
              onLabelsChange={handleLabelsChange}
              espTypes={distinctEspDescriptions || []}
              selectedEspTypes={selectedEspTypes}
              onEspTypesChange={handleEspTypesChange}
              connectionStatusOptions={connectionStatusOptions.map((option) => ({
                value: option.value,
                label: option.label,
                bgcolor: option.bgcolor,
              }))}
              selectedConnectionStatus={selectedConnectionStatus}
              onConnectionStatusChange={handleConnectionStatusChange}
              warmupStatusOptions={warmupStatusOptions || []}
              selectedWarmupStatus={selectedWarmupStatus}
              onWarmupStatusChange={handleWarmupStatusChange}
              subscriptionOptions={options || []}
              selectedSubscription={
                selectedPlanMetaData && selectedPlanMetaData.length > 0
                  ? selectedPlanMetaData
                  : ([] as string[])
              }
              onSubscriptionChange={handlePlanChange}
            />
            {selectedRows.length > 0 &&
              (hasPermission("mailboxes:edit") ||
                hasPermission("mailboxes:delete")) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="" size="sm">
                      <EllipsisVertical className="h-4 w-4" />
                      <span className="hidden sm:inline">Bulk Actions</span>
                      <span className="sm:hidden">Actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {hasPermission("mailboxes:edit") && (
                      <>
                        <DropdownMenuItem
                          onSelect={() => {
                            if (selectedRows.length > 0) {
                              const m = selectedRows[0];
                              setB2bSettingsMailbox({
                                id: m._id,
                                email: m.email,
                                firstName: m.firstName,
                                lastName: m.lastName,
                                planAssigned: !!(m.planId || m.addonPlan),
                                planMaxDailyLimit: m.customWarmupPlan?.maxDailyLimit,
                                selectedMailboxCount: selectedRows.length > 1 ? selectedRows.length : undefined,
                              });
                              setMailboxesToEnableAfterB2B(
                                selectedRows.map((r) => r._id)
                              );
                            }
                          }}
                        >
                          <Power className="h-4 w-4 mr-2" />
                          Enable Warmup
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handleBulkWarmup(false)}
                        >
                          <PowerOff className="h-4 w-4 mr-2" />
                          Disable Warmup
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handleBulkConnect(true)}
                        >
                          <PlugsConnected className="h-4 w-4 mr-2" />
                          Reconnect All
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handleBulkConnect(false)}
                        >
                          <Plugs className="h-4 w-4 mr-2" />
                          Disconnect All
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => setShowManageLabels(true)}
                        >
                          <Tag className="h-4 w-4 mr-2" />
                          Manage Labels
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handleAssignTemplates()}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Assign Templates
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => handlePlanAssignment()}
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Assign Subscriptions
                        </DropdownMenuItem>
                        {/* <DropdownMenuItem
                          onSelect={() => handleWarmupSpeed()}
                        >
                          <Gauge className="h-4 w-4 mr-2" />
                          Warmup Speed
                        </DropdownMenuItem> */}
                        <DropdownMenuItem
                          onSelect={() => {
                            if (selectedRows.length > 0) {
                              const m = selectedRows[0];
                              setB2bSettingsMailbox({
                                id: m._id,
                                email: m.email,
                                firstName: m.firstName,
                                lastName: m.lastName,
                                planAssigned: !!(m.planId || m.addonPlan),
                                planMaxDailyLimit: m.customWarmupPlan?.maxDailyLimit,
                                selectedMailboxCount: selectedRows.length > 1 ? selectedRows.length : undefined,
                              });
                              setMailboxesToEnableAfterB2B([]);
                            }
                          }}
                        >
                          <Settings2 className="h-4 w-4 mr-2" />
                          B2B Warmup Settings
                        </DropdownMenuItem>
                      </>
                    )}

                    {hasPermission("mailboxes:delete") && (
                      <DropdownMenuItem
                        onSelect={() => setShowBulkDeleteDialog(true)}
                        className="delete-button group"
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete Selected
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

            <ColumnVisibility
              gridRef={gridRef}
              storageKey="mailboxes-grid-columns"
              triggerClassName="[&>span]:hidden sm:[&>span]:block"
            />
          </div>
        </div>
      </div>

      {/* {isLoading || combinedLoading ? (
        <div className="flex justify-center items-center h-full">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : ( */}
      <DataTable
        ref={gridRef}
        rowData={mailboxes?.data ?? []}
        columnDefs={colDefs ?? []}
        height={550}
        showPagination={true}
        defaultColDef={{
          editable: false,
          resizable: true,
          filter: true,
          sortable: true,
          minWidth: 120,
          suppressSizeToFit: true,
        }}
        onSelectionChanged={onSelectionChanged}
        paginationProps={{
          totalEntries: mailboxes?.total || 0,
          pageSize: limit,
          totalPages: Math.ceil((mailboxes?.total || 0) / limit),
          currentPage: currentPage,
          onPageChange: (page: number) => {
            setCurrentPage(page);
          },
          rowsPerPage: limit,
          onRowsPerPageChange: (limit: number) => {
            setLimit(limit);
          },
        }}
        loading={isLoading || combinedLoading}
        gridOptions={{
          animateRows: false,
          suppressColumnVirtualisation: false,
          suppressRowVirtualisation: false,
          rowBuffer: 1000,
          getRowId: (params) => params.data?._id,
          suppressPropertyNamesCheck: true,
          suppressScrollOnNewData: false,
          enableCellTextSelection: true,
          alwaysShowVerticalScroll: true,
          suppressHorizontalScroll: false,
          ...gridOptions,
        }}
        rowHeight={48}
      />
      {/* )} */}

      {showEditMailboxDialog && (
        <MailboxWizard
          setShowAddMailboxDialog={(show) => setShowEditMailboxDialog(show)}
          showAddMailboxDialog={showEditMailboxDialog}
          mode="edit"
          initialData={mailboxToEdit}
        />
      )}

      {/* Bulk Delete Confirmation Popup */}
      {showBulkDeleteDialog && (
        <ConfirmActionPopup
          actionLabel="Delete Mailboxes"
          open={showBulkDeleteDialog}
          onOpenChange={setShowBulkDeleteDialog}
          title="Delete Selected Mailboxes"
          description={`Are you sure you want to delete the selected ${selectedRows.length} mailboxes? This action cannot be undone.`}
          onConfirm={handleBulkDelete}
          withTextConfirmation={true}
          textToConfirm="confirm"
        />
      )}

      {/* Single Delete Confirmation Popup */}
      {showSingleDeleteDialog && (
        <ConfirmActionPopup
          actionLabel="Delete Mailbox"
          open={showSingleDeleteDialog}
          onOpenChange={setShowSingleDeleteDialog}
          title="Delete Mailbox"
          description={
            <span>
              Are you sure you want to delete{" "}
              <span className="font-bold">{mailboxToDelete?.email}</span>{" "}
              mailbox? This action cannot be undone.
            </span>
          }
          onConfirm={confirmSingleDelete}
          withTextConfirmation={true}
          textToConfirm="confirm"
        />
      )}

      {/* {showManageLabels && (
        <ManageLabelsPopup
          open={showManageLabels}
          onOpenChange={setShowManageLabels}
          selectedMailboxIds={selectedRows.map((row) => row._id)}
        />
      )} */}
      {showManageLabels && (
        <ManageLabels
          open={showManageLabels}
          onOpenChange={setShowManageLabels}
          selectedRows={selectedRows.map((row) => ({
            _id: row._id,
            labels: row.labels,
          }))}
          labelType={LabelType.MAILBOX}
          afterChange={() => {
            gridRef.current?.api.deselectAll();
            setSelectedRows([]);
            setShowManageLabels(false);
          }}
        />
      )}

      {showAssignTemplates && (
        <AssignTemplatesDialog
          open={showAssignTemplates}
          onOpenChange={setShowAssignTemplates}
          selectedMailboxes={selectedRows}
          onAssignTemplates={handleTemplateAssignment}
        />
      )}

      {/* {showAssignPlans && (
        <AssignPlansDialog
          open={showAssignPlans}
          onOpenChange={setShowAssignPlans}
          selectedMailboxes={selectedRows}
          onAssignPlans={handlePlanAssignmentSubmit}
        />
      )} */}

      {showWarmupSpeed && (
        <WarmupSpeedDialog
          open={showWarmupSpeed}
          onOpenChange={setShowWarmupSpeed}
          selectedMailboxes={selectedRows}
          onApplySpeed={handleSpeedApplication}
        />
      )}

      {/* Mailbox Tutorial Dialog */}
      <InitialPageVisitGuideModal type="mailbox" />

      {/* Inbox Percentage Filter Popup */}
      {showInboxFilterPopup && (
        <InboxFilterPopup
          isOpen={showInboxFilterPopup}
          onClose={() => setShowInboxFilterPopup(false)}
          onApply={handleInboxFilterApply}
          onClear={handleInboxFilterClear}
          provider={filterProvider}
          currentMin={minInboxPercentage}
          currentMax={maxInboxPercentage}
        />
      )}

      {/* B2B Warmup Settings Dialog */}
      {b2bSettingsMailbox && (
        <WarmupB2BSettingsDialog
          open={!!b2bSettingsMailbox}
          onOpenChange={(open) => {
            if (!open) {
              setB2bSettingsMailbox(null);
              setMailboxesToEnableAfterB2B([]);
            }
          }}
          mailboxId={b2bSettingsMailbox.id}
          mailboxEmail={b2bSettingsMailbox.email}
          mailboxFirstName={b2bSettingsMailbox.firstName}
          mailboxLastName={b2bSettingsMailbox.lastName}
          selectedMailboxCount={b2bSettingsMailbox.selectedMailboxCount}
          planAssigned={b2bSettingsMailbox.planAssigned}
          planMaxDailyLimit={b2bSettingsMailbox.planMaxDailyLimit}
          onSaveAndEnableWarmup={
            mailboxesToEnableAfterB2B.length > 0
              ? handleB2BSaveAndEnableWarmup
              : undefined
          }
        />
      )}
    </>
  );
};

export default MailboxPageContent;
